
import { GoogleGenAI, Modality } from '@google/genai';
import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, AIPersona, UserProfile } from '../types';
import { base64ToBytes, decodeAudioData, getStudioApiKey } from '../utils/audio';

interface ChatSessionProps {
  persona: AIPersona;
  userProfile: UserProfile | null;
  ui: any;
  lang: string;
  voiceEnabled: boolean;
}

const ChatSession: React.FC<ChatSessionProps> = ({ persona, userProfile, ui, lang, voiceEnabled }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeVoiceMsgId, setActiveVoiceMsgId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const introText = lang === 'Spanish' 
      ? `Hola, soy ${persona.name}. ¿Cómo podemos colaborar hoy?`
      : `Hello, I'm ${persona.name}. Ready to collaborate?`;
    
    setMessages([{ id: 'intro', role: 'model', text: introText, timestamp: new Date() }]);
  }, [persona.id, lang]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  /**
   * VOICE PIPELINE:
   * Uses specialized gemini-2.5-flash-preview-tts for sub-second vocal response.
   * Enforces Australian lilt via the text prompt directive.
   */
  const executeVocalManifestation = async (text: string, messageId: string) => {
    const apiKey = getStudioApiKey();
    if (!apiKey || !text) return;

    setActiveVoiceMsgId(messageId);
    try {
      const ai = new GoogleGenAI({ apiKey });
      
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-preview-tts",
        contents: [{ parts: [{ text: `Speak in a natural, professional Australian lilt: ${text}` }] }],
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
              voiceConfig: {
                prebuiltVoiceConfig: { voiceName: persona.gender === 'Male' ? 'Kore' : 'Zephyr' },
              },
          },
        },
      });

      const audioData = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
      if (audioData) {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        const buf = await decodeAudioData(base64ToBytes(audioData), ctx, 24000, 1);
        const s = ctx.createBufferSource();
        s.buffer = buf;
        s.connect(ctx.destination);
        s.onended = () => {
            setActiveVoiceMsgId(null);
            ctx.close();
        };
        s.start();
      } else {
        setActiveVoiceMsgId(null);
      }
    } catch (e) {
      console.error("Vocal Pipeline Interrupted:", e);
      setActiveVoiceMsgId(null);
    }
  };

  /**
   * BRAIN PIPELINE:
   * Priority: Speed. Uses Gemini 3 Flash with thinkingBudget: 0.
   */
  const sendMessage = async () => {
    const apiKey = getStudioApiKey(); 
    if (!input.trim() || loading) return;
    
    if (!apiKey) {
      const errorMsg: ChatMessage = { 
        id: Date.now().toString(), 
        role: 'model', 
        text: "Vault Access Denied. Please check your Gemini API Key in the API Vault.", 
        isError: true, 
        timestamp: new Date() 
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    const userMsg: ChatMessage = { id: Date.now().toString(), role: 'user', text: input, timestamp: new Date() };
    const modelMsgId = (Date.now() + 1).toString();
    
    // Optimistic UI push
    setMessages(prev => [...prev, userMsg, { id: modelMsgId, role: 'model', text: '', timestamp: new Date() }]);
    setInput('');
    setLoading(true);

    try {
      const ai = new GoogleGenAI({ apiKey });
      
      // Use the absolute fastest text-only model
      const modelToUse = 'gemini-3-flash-preview';
      
      const historyContents = messages.map(m => ({
        role: m.role,
        parts: [{ text: m.text }]
      }));

      const personaContext = `Identity: ${persona.name}. Traits: ${persona.traits.join(", ")}. Background: ${persona.background}. Origin: Sydney. Creative Lead at GrooveFX. Professional, visionary, sharp. Language: ${lang}.`;

      const response = await ai.models.generateContent({
        model: modelToUse,
        contents: [
          ...historyContents,
          { role: 'user', parts: [{ text: input }] }
        ],
        config: { 
            systemInstruction: personaContext,
            // BOLT MODE: Disable internal thinking for sub-second text output
            thinkingConfig: { thinkingBudget: 0 }
        }
      });

      const responseText = response.text || "Transmission received.";

      // Update text bubble immediately
      setMessages(prev => prev.map(m => m.id === modelMsgId ? { ...m, text: responseText } : m));
      
      // Fire the voice manifestation in parallel/sequential non-blocking
      if (voiceEnabled) {
        executeVocalManifestation(responseText, modelMsgId);
      }
    } catch (e: any) { 
        console.error("Neural Bolt Failure:", e);
        setMessages(prev => prev.map(m => m.id === modelMsgId ? { 
          ...m, 
          text: `Neural Handshake Failed (Error ${e.status || '500'}). Pipeline congestion detected.`, 
          isError: true 
        } : m));
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-black/50 rounded-[5rem] border border-white/10 shadow-3xl overflow-hidden min-h-[600px] animate-in fade-in duration-1000">
      
      {/* MESSAGES VIEWPORT */}
      <div className="flex-1 overflow-y-auto p-12 md:p-16 space-y-12 custom-scrollbar">
        {messages.map((m, index) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`relative max-w-[85%] px-12 py-9 rounded-[3.5rem] border transition-all duration-500 ${m.role === 'user' ? 'bg-groove-accent/10 border-groove-accent/30 shadow-[0_0_30px_rgba(0,210,255,0.1)]' : 'bg-white/5 border-white/10 shadow-2xl flex gap-8'}`}>
              
              {m.role === 'model' && persona.avatar && (
                 <div className="flex flex-col items-center shrink-0">
                    <img src={persona.avatar} className="w-16 h-16 rounded-full border border-white/10 hidden sm:block object-cover shadow-2xl" alt={persona.name} />
                 </div>
              )}

              <div className="flex flex-col gap-4">
                  {/* Bolt-Fast Text Manifestation */}
                  <p className={`text-xl md:text-2xl leading-relaxed font-light ${m.isError ? 'text-red-400' : 'text-slate-100'}`}>
                    {m.text || (loading && index === messages.length - 1 ? '...' : '')}
                  </p>
                  
                  {/* Vocal Uplink Visual (Anchored to Bubble) */}
                  {activeVoiceMsgId === m.id && (
                    <div className="flex items-center gap-4 text-groove-pink text-[9px] font-black uppercase tracking-[0.4em] animate-pulse mt-4 bg-white/5 px-6 py-2 rounded-full border border-white/10 w-fit">
                      <div className="flex gap-1">
                        <div className="w-1 h-3 bg-groove-pink rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
                        <div className="w-1 h-5 bg-groove-pink rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-1 h-2 bg-groove-pink rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                      Neural Lilt Active
                    </div>
                  )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT HUB */}
      <div className="p-12 border-t border-white/10 flex gap-10 bg-black/40 backdrop-blur-3xl">
        <textarea 
          value={input} 
          onChange={e => setInput(e.target.value)} 
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())} 
          className="flex-1 bg-white/5 border border-white/10 rounded-[2.5rem] px-12 py-7 text-xl text-white outline-none resize-none focus:border-groove-accent transition-all placeholder:text-slate-700 custom-scrollbar" 
          rows={1} 
          placeholder={`${persona.name} is standing by...`} 
        />
        <button onClick={sendMessage} className="w-24 h-24 bg-white text-black rounded-[2rem] flex items-center justify-center hover:bg-groove-pink hover:text-white hover:scale-105 active:scale-90 transition-all shadow-3xl">
          <i className="fa-solid fa-paper-plane text-3xl"></i>
        </button>
      </div>
    </div>
  );
};

export default ChatSession;
